/**
 * WORKSPACE/PERFIL — re-exporta `@imobdesk/contracts`.
 * UX: form aceita CPF formatado; API exige 11 dígitos crus.
 */
import { z } from 'zod'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { structuredErrorResponseSchema, workspace } from '@imobdesk/contracts'

export const userProfileSchema = workspace.userProfileSchema

export const profileResponseSchema = workspace.profileResponseSchema

export const changePasswordRequestSchema = workspace.updatePasswordRequestSchema

/** UX: validação de formulário — request API usa `changePasswordRequestSchema`. */
export const changePasswordFormSchema = changePasswordRequestSchema

export function mapChangePasswordFormToApiRequest(
  input: ChangePasswordForm,
): ChangePasswordRequest {
  return changePasswordRequestSchema.parse(input)
}

export const changePasswordResponseSchema =
  workspace.changePasswordResponseSchema

export const changePasswordErrorSchema = structuredErrorResponseSchema

export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>
export type ChangePasswordForm = z.infer<typeof changePasswordFormSchema>
export type ChangePasswordResponse = z.infer<
  typeof changePasswordResponseSchema
>
export type ChangePasswordError = z.infer<typeof changePasswordErrorSchema>

export const updateProfileRequestSchema = workspace.updateProfileRequestSchema

/** UX: CPF mascarado no input com validação de dígitos verificadores. */
export const updateProfileFormSchema = z.object({
  nome: z.string().min(1, 'Nome é um campo obrigatório'),
  sobrenome: z.string().min(1, 'Sobrenome é um campo obrigatório'),
  cpf: z
    .string()
    .min(1, 'CPF é obrigatório')
    .refine(
      (val) => {
        const cleaned = val.replace(/\D/g, '')
        return cleaned.length === 11
      },
      {
        message: 'CPF deve conter 11 dígitos',
      },
    )
    .refine(
      (val) => {
        const cleaned = val.replace(/\D/g, '')
        if (/^(\d)\1{10}$/.test(cleaned)) {
          return false
        }
        let sum = 0
        for (let i = 0; i < 9; i++) {
          sum += parseInt(cleaned.charAt(i)) * (10 - i)
        }
        let digit = 11 - (sum % 11)
        if (digit >= 10) digit = 0
        if (digit !== parseInt(cleaned.charAt(9))) return false

        sum = 0
        for (let i = 0; i < 10; i++) {
          sum += parseInt(cleaned.charAt(i)) * (11 - i)
        }
        digit = 11 - (sum % 11)
        if (digit >= 10) digit = 0
        return digit === parseInt(cleaned.charAt(10))
      },
      {
        message: 'CPF inválido',
      },
    ),
})

export function mapUpdateProfileFormToApiRequest(
  input: UpdateProfileForm,
): UpdateProfileRequest {
  return updateProfileRequestSchema.parse({
    nome: input.nome,
    sobrenome: input.sobrenome,
    cpf: input.cpf.replace(/\D/g, ''),
  })
}

export const updateProfileResponseSchema = workspace.updateProfileResponseSchema

export const updateProfileErrorSchema = structuredErrorResponseSchema

export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>
export type UpdateProfileForm = z.infer<typeof updateProfileFormSchema>
export type UpdateProfileResponse = z.infer<typeof updateProfileResponseSchema>
export type UpdateProfileError = z.infer<typeof updateProfileErrorSchema>

export type UserProfile = z.infer<typeof userProfileSchema>
export type ProfileResponse = z.infer<typeof profileResponseSchema>

export const changeEmailRequestSchema = workspace.changeEmailRequestSchema

/** UX: validação de formulário — request API usa `changeEmailRequestSchema`. */
export const changeEmailFormSchema = changeEmailRequestSchema

export function mapChangeEmailFormToApiRequest(
  input: ChangeEmailForm,
): ChangeEmailRequest {
  return changeEmailRequestSchema.parse(input)
}

export const changeEmailConfirmSchema = workspace.changeEmailConfirmSchema

export const changeEmailFinalizeSchema = workspace.changeEmailConfirmSchema

export const changeEmailRequestResponseSchema =
  workspace.changeEmailRequestResponseSchema

export const changeEmailFinalizeResponseSchema =
  workspace.changeEmailFinalizeResponseSchema

export const changeEmailErrorSchema = structuredErrorResponseSchema

export type ChangeEmailRequest = z.infer<typeof changeEmailRequestSchema>
export type ChangeEmailForm = z.infer<typeof changeEmailFormSchema>
export type ChangeEmailConfirmInput = z.infer<typeof changeEmailConfirmSchema>
export type ChangeEmailFinalizeInput = z.infer<typeof changeEmailFinalizeSchema>
export type ChangeEmailRequestResponse = z.infer<
  typeof changeEmailRequestResponseSchema
>
export type ChangeEmailFinalizeResponse = z.infer<
  typeof changeEmailFinalizeResponseSchema
>
export type ChangeEmailError = z.infer<typeof changeEmailErrorSchema>

export const changePhoneRequestSchema = workspace.changePhoneRequestSchema

export type PhoneChangeFlow = z.infer<typeof workspace.phoneChangeFlowSchema>

/** UX: validação amigável de telefone internacional no form. */
export const changePhoneFormSchema = z.object({
  newPhone: z
    .string()
    .min(1, 'Celular é obrigatório')
    .refine(
      (val) => {
        try {
          return isValidPhoneNumber(val)
        } catch {
          return false
        }
      },
      { message: 'Digite um celular válido' },
    ),
})

export function mapChangePhoneFormToApiRequest(
  input: ChangePhoneForm,
): ChangePhoneRequest {
  return changePhoneRequestSchema.parse({ newPhone: input.newPhone })
}

export const changePhoneConfirmSchema = workspace.changePhoneConfirmSchema

export const changePhoneFinalizeSchema = workspace.changePhoneConfirmSchema

export const changePhoneRequestResponseSchema =
  workspace.changePhoneRequestResponseSchema

export const changePhoneConfirmResponseSchema =
  workspace.changePhoneConfirmResponseSchema

export const changePhoneFinalizeResponseSchema =
  workspace.changePhoneFinalizeResponseSchema

export const changePhoneErrorSchema = structuredErrorResponseSchema

export type ChangePhoneRequest = z.infer<typeof changePhoneRequestSchema>
export type ChangePhoneForm = z.infer<typeof changePhoneFormSchema>
export type ChangePhoneConfirmInput = z.infer<typeof changePhoneConfirmSchema>
export type ChangePhoneFinalizeInput = z.infer<typeof changePhoneFinalizeSchema>
export type ChangePhoneRequestResponse = z.infer<
  typeof changePhoneRequestResponseSchema
>
export type ChangePhoneConfirmResponse = z.infer<
  typeof changePhoneConfirmResponseSchema
>
export type ChangePhoneFinalizeResponse = z.infer<
  typeof changePhoneFinalizeResponseSchema
>
export type ChangePhoneError = z.infer<typeof changePhoneErrorSchema>

export const registerPhoneRequestSchema = workspace.registerPhoneRequestSchema
export const registerPhoneConfirmSchema = workspace.registerPhoneConfirmSchema
export const registerPhoneRequestResponseSchema =
  workspace.registerPhoneRequestResponseSchema
export const registerPhoneFinalizeResponseSchema =
  workspace.registerPhoneFinalizeResponseSchema

export type RegisterPhoneConfirmInput = z.infer<
  typeof registerPhoneConfirmSchema
>
export type RegisterPhoneRequestResponse = z.infer<
  typeof registerPhoneRequestResponseSchema
>
export type RegisterPhoneFinalizeResponse = z.infer<
  typeof registerPhoneFinalizeResponseSchema
>

export const uploadAvatarRequestSchema = workspace.uploadAvatarRequestSchema

export const uploadAvatarResponseSchema = workspace.uploadAvatarResponseSchema

export const uploadAvatarErrorSchema = structuredErrorResponseSchema

export const updateAvatarRequestSchema = workspace.updateAvatarRequestSchema

/** UX: validação de formulário — request API usa `updateAvatarRequestSchema`. */
export const updateAvatarFormSchema = updateAvatarRequestSchema

export function mapUpdateAvatarFormToApiRequest(
  input: UpdateAvatarForm,
): UpdateAvatarRequest {
  return updateAvatarRequestSchema.parse(input)
}

export const updateAvatarResponseSchema = workspace.updateAvatarResponseSchema

export const updateAvatarErrorSchema = structuredErrorResponseSchema

export type UploadAvatarRequestInput = z.infer<typeof uploadAvatarRequestSchema>
export type UploadAvatarResponse = z.infer<typeof uploadAvatarResponseSchema>
export type UploadAvatarError = z.infer<typeof uploadAvatarErrorSchema>
export type UpdateAvatarRequest = z.infer<typeof updateAvatarRequestSchema>
export type UpdateAvatarForm = z.infer<typeof updateAvatarFormSchema>
export type UpdateAvatarResponse = z.infer<typeof updateAvatarResponseSchema>
export type UpdateAvatarError = z.infer<typeof updateAvatarErrorSchema>
